import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2,
  Check,
  X,
  Eye,
  EyeOff,
  FileText,
  Banknote,
} from "lucide-react";

import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  listPaiementsHorsSysteme,
  validerPaiementHorsSysteme,
  rejeterPaiementHorsSysteme,
  getPreuveObjectUrl,
  type PaiementHorsSysteme,
} from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { resteDeclaration } from "@/lib/payer-validation";
import AnneeInactiveBanner from "@/components/AnneeInactiveBanner";
import { useAnneeScope } from "@/lib/annee-scope";

function fcfa(n: number) {
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}

const STATUTS = [
  { value: "EN_ATTENTE_VALIDATION", label: "À valider" },
  { value: "VALIDE", label: "Validés" },
  { value: "REJETE", label: "Rejetés" },
] as const;

const CANAL_LABEL: Record<string, string> = {
  VIREMENT: "Virement",
  DEPOT_BANQUE: "Dépôt banque",
  CHEQUE: "Chèque",
  ESPECES: "Espèces",
  AUTRE: "Autre",
};

export default function PaiementsHorsSystemePage() {
  const [statut, setStatut] = useState<string>("EN_ATTENTE_VALIDATION");
  const [apercu, setApercu] = useState<{ id: string; url: string; type: string } | null>(null);
  const [rejet, setRejet] = useState<{ id: string; motif: string } | null>(null);
  const qc = useQueryClient();
  const { isActiveAnnee } = useAnneeScope();

  const { data, isLoading } = useQuery({
    queryKey: ["paiements-hors-systeme", statut],
    queryFn: () => listPaiementsHorsSysteme(statut),
  });

  function invalider() {
    qc.invalidateQueries({ queryKey: ["paiements-hors-systeme"] });
  }

  const valider = useMutation({
    mutationFn: (id: string) => validerPaiementHorsSysteme(id),
    onSuccess: () => {
      toast.success("Paiement validé : il compte désormais pour le motif.");
      invalider();
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  const rejeter = useMutation({
    mutationFn: (v: { id: string; motif: string }) => rejeterPaiementHorsSysteme(v.id, v.motif),
    onSuccess: () => {
      toast.success("Paiement rejeté.");
      setRejet(null);
      invalider();
    },
    onError: (e: unknown) => toast.error(friendlyError(e)),
  });

  async function voirPreuve(p: PaiementHorsSysteme) {
    if (apercu?.id === p.id) {
      URL.revokeObjectURL(apercu.url);
      setApercu(null);
      return;
    }
    try {
      const url = await getPreuveObjectUrl(p.id);
      setApercu({ id: p.id, url, type: p.preuveContentType });
    } catch {
      toast.error("Impossible de charger la preuve.");
    }
  }

  const items = data ?? [];

  return (
    <EcoleShell>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-navy">Paiements hors système</h1>
        <p className="text-sm text-slate-600">
          Virements, dépôts en banque… déclarés par les parents avec une preuve, à
          valider avant qu'ils ne comptent pour le motif.
        </p>
      </div>

      {!isActiveAnnee && <AnneeInactiveBanner />}

      <div className="mb-4 flex gap-1">
        {STATUTS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatut(s.value)}
            data-testid={`filter-${s.value}`}
            className={`text-xs px-3 py-1.5 rounded border ${
              statut === s.value
                ? "bg-navy text-white border-navy"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">
          <Banknote className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          Aucune demande dans cette catégorie.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((p) => {
            // SCOM-04 : le reste dû se lit avant de valider ; une déclaration
            // qui le dépasse, ou sur un motif soldé, se rejette (le serveur
            // refuse aussi la validation).
            const r = p.statut === "EN_ATTENTE_VALIDATION" ? resteDeclaration(p) : null;
            const bloque = !!r && (r.solde || r.depasse);
            return (
            <Card key={p.id} className="p-4" data-testid={`phs-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-navy">
                    {fcfa(p.montantXof)} · {p.motifLibelle ?? "Non renseigné"}
                    {p.trancheLibelle ? ` · ${p.trancheLibelle}` : ""}
                  </p>
                  {r ? (
                    <p
                      className={`text-xs mt-1 ${bloque ? "text-destructive font-semibold" : "text-slate-600"}`}
                      data-testid={`reste-${p.id}`}
                    >
                      Reste dû : {fcfa(r.reste)}
                      {r.solde ? " · déjà soldé, à rejeter" : ""}
                      {r.depasse ? " · montant supérieur au reste, à rejeter" : ""}
                    </p>
                  ) : null}
                  <p className="text-sm text-slate-600">
                    {p.eleveNom ?? ""} {p.elevePrenoms ?? ""}
                    {p.eleveMatricule ? ` · ${p.eleveMatricule}` : ""}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {CANAL_LABEL[p.canal] ?? p.canal}
                    {p.banque ? ` · ${p.banque}` : ""}
                    {p.referenceExterne ? ` · réf ${p.referenceExterne}` : ""}
                    {p.datePaiement ? ` · ${p.datePaiement}` : ""}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Réf {p.reference} · par {p.payerNom ?? ""} {p.payerPrenoms ?? ""}
                  </p>
                  {p.statut === "REJETE" && p.motifRejet ? (
                    <p className="text-xs text-destructive mt-1">Rejet : {p.motifRejet}</p>
                  ) : null}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => voirPreuve(p)}
                    data-testid={`voir-${p.id}`}
                  >
                    {apercu?.id === p.id ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                    <span className="ml-1">Preuve</span>
                  </Button>

                  {p.statut === "EN_ATTENTE_VALIDATION" ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-sage hover:bg-sage/90 text-white"
                        disabled={valider.isPending || !isActiveAnnee || bloque}
                        onClick={() => valider.mutate(p.id)}
                        data-testid={`valider-${p.id}`}
                      >
                        <Check className="w-4 h-4" />
                        <span className="ml-1">Valider</span>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/40"
                        disabled={!isActiveAnnee}
                        onClick={() => setRejet({ id: p.id, motif: "" })}
                        data-testid={`rejeter-${p.id}`}
                      >
                        <X className="w-4 h-4" />
                        <span className="ml-1">Rejeter</span>
                      </Button>
                    </>
                  ) : (
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        p.statut === "VALIDE"
                          ? "bg-sage/15 text-sage"
                          : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {p.statut === "VALIDE" ? "Validé" : "Rejeté"}
                    </span>
                  )}
                </div>
              </div>

              {apercu?.id === p.id ? (
                <div className="mt-3 border-t pt-3" data-testid={`apercu-${p.id}`}>
                  {apercu.type === "application/pdf" ? (
                    <iframe
                      title={`Preuve ${p.reference}`}
                      src={apercu.url}
                      className="w-full h-96 rounded border"
                    />
                  ) : (
                    <img
                      src={apercu.url}
                      alt={`Preuve ${p.reference}`}
                      className="max-h-96 rounded border"
                    />
                  )}
                  <a
                    href={apercu.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-navy underline inline-flex items-center gap-1 mt-2"
                  >
                    <FileText className="w-3 h-3" /> Ouvrir dans un onglet
                  </a>
                </div>
              ) : null}

              {rejet?.id === p.id ? (
                <div className="mt-3 border-t pt-3 flex items-center gap-2" data-testid={`rejet-form-${p.id}`}>
                  <Input
                    value={rejet.motif}
                    onChange={(e) => setRejet({ id: p.id, motif: e.target.value })}
                    placeholder="Motif du rejet"
                    className="flex-1"
                    data-testid={`rejet-motif-${p.id}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={rejeter.isPending || rejet.motif.trim().length === 0 || !isActiveAnnee}
                    onClick={() => rejeter.mutate({ id: p.id, motif: rejet.motif.trim() })}
                    data-testid={`rejet-confirm-${p.id}`}
                  >
                    Confirmer le rejet
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRejet(null)}>
                    Annuler
                  </Button>
                </div>
              ) : null}
            </Card>
            );
          })}
        </div>
      )}
    </EcoleShell>
  );
}
